import { getValetTicketPhotos } from '@jq4race/api-client';
import { useQuery } from '@tanstack/react-query';

/**
 * useTicketPhotos — ticket-photos-for-valet
 *
 * Fetches the least-privilege photo listing (photos + incidentPhotos) for
 * a ticket, gated by `ticketId` being non-null so it only fires while the
 * caller actually needs the photos (e.g. the delivery confirm dialog is
 * open) — never a background/prefetch request.
 *
 * `staleTime: 0` is intentional: the backend signs each URL with a 5-minute
 * TTL, so cached data must never be reused silently once it may have gone
 * stale — every (re)mount re-requests fresh signed URLs. This is a
 * read-only, non-blocking aid (REQ: viewing photos never blocks delivery),
 * so a single retry is enough — the caller renders its own inline
 * error/retry affordance instead of surfacing a hard failure.
 */
export function useTicketPhotos(ticketId: string | null) {
  return useQuery({
    queryKey: ['ticket-photos', ticketId] as const,
    queryFn: () => getValetTicketPhotos(ticketId as string),
    enabled: ticketId !== null,
    retry: 1,
    staleTime: 0,
  });
}
